// Header Component
import { User, LogOut, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '../ui/button';
import { useAgentStore } from '../../stores/agentStore';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import type { ThemeMode } from '../../stores/uiStore';
import { useLocation } from 'react-router-dom';

const THEME_CYCLE: ThemeMode[] = ['light', 'dark', 'system'];
const THEME_ICONS = { light: Sun, dark: Moon, system: Monitor } as const;
const THEME_LABELS = { light: 'Tema claro', dark: 'Tema oscuro', system: 'Tema del sistema' } as const;

export function Header() {
  const location = useLocation();
  const { status } = useAgentStore();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useUIStore();

  const agentIndicator = {
    connected: { dotClass: 'bg-green-500', label: 'Conectado' },
    degraded: { dotClass: 'bg-amber-500', label: 'Degradado' },
    unauthorized: { dotClass: 'bg-red-500', label: 'Sin autorización' },
    timeout: { dotClass: 'bg-amber-500', label: 'Timeout' },
    error: { dotClass: 'bg-red-500', label: 'Error interno' },
    disconnected: { dotClass: 'bg-red-500', label: 'Desconectado' },
  }[status.status];

  const handleLogout = async () => {
    // NAV-02: Si el usuario está en el editor con cambios sin guardar, confirmar antes de logout
    const isInEditor = location.pathname.startsWith('/workflows/') && location.pathname !== '/workflows';
    if (isInEditor) {
      // Verificar flag de cambios sin guardar (establecido por WorkflowEditorPage)
      const isDirty = (window as unknown as { __workflowEditorDirty?: boolean }).__workflowEditorDirty;
      if (isDirty) {
        const confirmed = window.confirm('Tienes cambios sin guardar. ¿Estás seguro de que deseas cerrar sesión?');
        if (!confirmed) return;
      }
    }

    try {
      await logout();
    } catch {
      // M-04: Incluso si logout falla, ProtectedRoute redirigirá al login
    }
    // P-10: No llamar navigate('/login'). ProtectedRoute detecta user=null
    // y redirige automáticamente, evitando doble redirección con returnTo no deseado.
  };

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme);
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]);
  };

  const ThemeIcon = THEME_ICONS[theme];

  return (
    <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-6 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex-1" />

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700">
          <div
            className={`h-2.5 w-2.5 rounded-full ${agentIndicator.dotClass}`}
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {agentIndicator.label}
          </span>
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-800">
          <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {user?.email?.split('@')[0] || 'Usuario'}
          </span>
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={cycleTheme}
          title={THEME_LABELS[theme]}
          aria-label={THEME_LABELS[theme]}
        >
          <ThemeIcon className="h-5 w-5" />
        </Button>

        <Button variant="ghost" size="icon" onClick={handleLogout} title="Cerrar sesión" aria-label="Cerrar sesión">
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
}
